import { useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Bundle, ContentType, ProductModule, SoftwarePlan } from 'src/api/contentful/contentTypes.cloverdotcom';
import { useCMSFilteredByDcsId, useCMSFilteredByDcsIds } from 'src/hooks/UseCMSByFilters';
import { BundlePricings, DcsPricingQuery, ProductPricings, useDCS } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { INSTALLMENT_LENGTHS, IncludedProduct, InstallmentPlanCode, RecommendedAddonProduct } from 'src/services/dcs/bundle';
import { CartContextProps, initialCartModel } from './CartContext';
import { setCartDataToLocalStorage } from './Util';
import { BundleModel, CartModel, InstallmentPlanModel, ProductModel, PromoModel, SoftwarePlanModel, UpsellModel } from './models';

type ContentBundle = FlatEntry<Bundle>;
type ContentProduct = FlatEntry<ProductModule>;
type ContentSoftwarePlan = FlatEntry<SoftwarePlan>;

const initialDcsParam: DcsPricingQuery = { bundleIds: [], productIds: [] };

export const useCartInternal = (initialCartData: CartModel = initialCartModel): CartContextProps => {
  const [cartData, setCartData] = useState<CartModel>(initialCartData);
  const [pricingQuery, setPricingQuery] = useState<DcsPricingQuery>(initialDcsParam);
  const dcs = useDCS(pricingQuery);
  const bundlePricings = dcs.bundlesPricings[0];
  const productsPricings = bundlePricings ? dcs.productsPricings.filter(Boolean) as ProductPricings[] : [];
  const contentBundle = useCMSFilteredByDcsId(ContentType.BUNDLE, pricingQuery.bundleIds[0]);
  const contentSoftwarePlan = useCMSFilteredByDcsId(ContentType.SOFTWARE_PLAN, bundlePricings?.softwarePlan.id);
  const contentProducts = useCMSFilteredByDcsIds(ContentType.PRODUCT_MODULE, pricingQuery.productIds);

  useEffect(() => {
    if (!bundlePricings) return;
    const items = [...bundlePricings.includedProducts, ...bundlePricings.recommendedAddons];
    setPricingQuery(prev => ({ ...prev, productIds: items.map(({ product }) => product.id) }));
  }, [bundlePricings]);

  useDeepCompareEffect(() => {
    if (!bundlePricings || !contentBundle || !contentSoftwarePlan || !contentProducts) return;
    setCartData(prev => updateCart({ ...prev, isLoading: false }, bundlePricings, productsPricings, contentBundle.data, contentSoftwarePlan.data, contentProducts.data));
  }, [productsPricings, contentBundle, contentSoftwarePlan, contentProducts]);

  useDeepCompareEffect(() => {
    setCartDataToLocalStorage(cartData);
  }, [cartData]);

  const addBundleById = (bundleId: string) => {
    setCartData(prev => emptyCart(prev, true));
    setPricingQuery(initialDcsParam);
    setTimeout(() => setPricingQuery(prev => ({ ...prev, bundleIds: [bundleId] })));
  };

  return {
    ...cartData,
    emptyCart: () => setCartData(emptyCart),
    addBundleById,
    addProduct: (product: UpsellModel) => setCartData(prev => addProduct(product, prev)),
    updateProductQuantityById: (productId: string, quantity: number) => (
      setCartData(prev => updateProductQuantityById(productId, quantity, prev)
    )),
    removeProductById: (productId: string) => setCartData(prev => removeProductById(productId, prev)),
    setTaxRate: (tax: number) => setCartData(prev => setTaxRate(tax, prev)),
    addPromo: (promo: PromoModel) => setCartData(prev => addPromo(promo, prev)),
    removePromo: () => setCartData(prev => removePromo(prev)),
  };
};

const updateCheckout = (prev: CartModel): CartModel => {
  const products = [...prev.cart.bundle?.hardware || [], ...prev.cart.products];
  const count = products.reduce((total, { quantity }) => total + quantity, 0);
  const subtotal = products.reduce((total, { totalCost, quantity }) => total + totalCost * quantity, 0);
  return { ...prev, checkout: { ...prev.checkout, count, subtotal } };
};

const emptyCart = (prev: CartModel, isLoading: boolean = false): CartModel => {
  const { tax, promo } = prev.checkout;
  return {
    isLoading,
    cart: initialCartModel.cart,
    checkout: { ...initialCartModel.checkout, tax, promo },
  };
};

const addProduct = (product: UpsellModel, prev: CartModel): CartModel => {
  const products = [...prev.cart.products];
  const index = products.findIndex(p => p.id === product.id);
  if (index === -1) {
    products.push({ ...product, quantity: 1 });
  } else {
    products[index] = { ...products[index], quantity: products[index].quantity + 1 };
  }
  const upsell = prev.cart.upsell.filter((u => u.id !== product.id));
  return updateCheckout({ ...prev, cart: { ...prev.cart, products, upsell } });
};

const updateProductQuantityById = (productId: string, quantity: number, prev: CartModel): CartModel => {
  const updateQuantity = (product: ProductModel) => ({
    ...product, quantity: product.id === productId ? quantity : product.quantity,
  });
  const products = prev.cart.products.map(updateQuantity);
  const hardware = prev.cart.bundle?.hardware.map(updateQuantity);
  const bundle = prev.cart.bundle && hardware ? { ...prev.cart.bundle, hardware } : null;
  return updateCheckout({ ...prev, cart: { ...prev.cart, bundle, products } });
};

const removeProductById = (productId: string, prev: CartModel): CartModel => {
  const { products, upsell } = prev.cart.products.reduce((acc, product) => {
    if (productId === product.id) {
      const { quantity, ...rest } = product;
      acc.upsell.push(rest);
    } else {
      acc.products.push({ ...product });
    }
    return acc;
  }, {
    products: [] as ProductModel[],
    upsell: [...prev.cart.upsell] as UpsellModel[],
  });
  return updateCheckout({ ...prev, cart: { ...prev.cart, products, upsell } });
};

const setTaxRate = (tax: number, prev: CartModel): CartModel => {
  return { ...prev, checkout: { ...prev.checkout, tax } };
};

const addPromo = (promo: PromoModel, prev: CartModel): CartModel => {
  const mergedPromo = { ...prev.checkout.promo, ...promo };
  return { ...prev, checkout: { ...prev.checkout, promo: { ...mergedPromo, applied: mergedPromo.applied || mergedPromo.program } } };
};

const removePromo = (prev: CartModel): CartModel => {
  return { ...prev, checkout: { ...prev.checkout, promo: { ...prev.checkout.promo, applied: prev.checkout.promo.program } } };
};

const convertAddonToUpsell = (
  { product: { id, name, fdmpId } }: RecommendedAddonProduct,
  productsPricings: ProductPricings[],
  contentProducts: ContentProduct[] = []
): UpsellModel | undefined => {
  const productsPricing = productsPricings.find(p => p.id === id);
  if (!productsPricing) return;
  const contentProduct = contentProducts.find(p => p.dcsId === id);
  return {
    id,
    fdmpId,
    name: contentProduct?.name || name,
    imageURL: contentProduct?.images?.[0]?.file?.url || null,
    totalCost: productsPricing.basePrice.value,
  };
};

const convertIncludedProductToProduct = (
  { quantity, ...rest }: IncludedProduct,
  productsPricings: ProductPricings[],
  contentProducts?: ContentProduct[]
): ProductModel | undefined => {
  const upsell = convertAddonToUpsell(rest, productsPricings, contentProducts);
  if (!upsell) return;
  return { ...upsell, quantity };
};

const updateCart = (
  prev: CartModel,
  bundlePricings: BundlePricings,
  productsPricings: ProductPricings[],
  contentBundle?: ContentBundle,
  contentSoftwarePlan?: ContentSoftwarePlan,
  contentProducts?: ContentProduct[]
): CartModel => {
  const {
    id,
    name,
    includedProducts,
    recommendedAddons,
    monthlyAdditionalPrice,
    keyedInRate,
    inPersonRate,
    softwarePlan: software,
    installmentPlans: ips,
  } = bundlePricings;
  const transactions = {
    inPerson: { cost: inPersonRate.fixedAmount.value, percentage: inPersonRate.percentage },
    keyedIn: { cost: keyedInRate.fixedAmount.value, percentage: keyedInRate.percentage },
  };
  const hardware = includedProducts.map(prod => convertIncludedProductToProduct(prod, productsPricings, contentProducts)).filter(Boolean) as ProductModel[];
  const upsell = recommendedAddons.map(addon => addon.product.isSelfServicePurchaseEnabled && convertAddonToUpsell(addon, productsPricings, contentProducts)).filter(Boolean) as UpsellModel[];
  const bundle: BundleModel = { id, name: contentBundle?.title || name, hardware, cartBusinessType: contentBundle?.cartBusinessType, pricingPageLink: contentBundle?.pricingPageLink };
  const softwarePlan: SoftwarePlanModel = { id: software.id, name: contentSoftwarePlan?.name || software.name, monthly: monthlyAdditionalPrice.value, transactions };
  const installmentPlans: InstallmentPlanModel[] = Object.keys(ips).map((key: InstallmentPlanCode) => ({
    id: key,
    months: INSTALLMENT_LENGTHS[key],
    cost: ips[key].value,
  }));
  return updateCheckout({
    ...prev,
    cart: { bundle, softwarePlan, products: [], upsell },
    checkout: { ...prev.checkout, installmentPlans },
  });
};
